import { getAllAdminOrganizations } from "@/api/admin/organization";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { OrganizationRaw } from "@/domain/Organization";
import { useEffect, useMemo, useState } from "react";

type OrgItem = { id: string; name: string };
type OrgAssignmentState = { left: OrgItem[]; right: OrgItem[] };

export const useOrgAssignment = ({
  currentAssignedOrgs,
  isCurrentAssignedOrgsLoaded,
  onDiffChange,
}: {
  currentAssignedOrgs: { org_id: string; name: string }[];
  isCurrentAssignedOrgsLoaded: boolean;
  onDiffChange?: (diff: OrgAssignmentState) => void;
}) => {
  const [showAllOrgs, setShowAllOrgs] = useState(false);
  const { organizations: invitedOrgs } = useAppOrgsAndInvitedOrgs();
  const { showErrorNotification } = useAppNotificationManager();
  const [{ allOrgs, isAllOrgsLoadStarted, isAllOrgsLoaded }, setAllOrgs] =
    useState<{
      allOrgs: OrganizationRaw[];
      isAllOrgsLoadStarted: boolean;
      isAllOrgsLoaded: boolean;
    }>({
      allOrgs: [],
      isAllOrgsLoadStarted: false,
      isAllOrgsLoaded: false,
    });

  useEffect(() => {
    if (!showAllOrgs || isAllOrgsLoadStarted) return;
    setAllOrgs({
      allOrgs: [],
      isAllOrgsLoadStarted: true,
      isAllOrgsLoaded: false,
    });
    const fetchAllOrgs = async () => {
      try {
        const fetched = await getAllAdminOrganizations((loaded) =>
          setAllOrgs({
            allOrgs: loaded,
            isAllOrgsLoadStarted: true,
            isAllOrgsLoaded: false,
          })
        );
        setAllOrgs({
          allOrgs: fetched,
          isAllOrgsLoadStarted: true,
          isAllOrgsLoaded: true,
        });
      } catch (error) {
        setAllOrgs({
          allOrgs: [],
          isAllOrgsLoadStarted: false,
          isAllOrgsLoaded: false,
        });
        setShowAllOrgs(false);
        showErrorNotification("全組織の取得に失敗しました", error);
      }
    };
    fetchAllOrgs();
  }, [showAllOrgs, isAllOrgsLoadStarted, showErrorNotification]);

  const [diffAssignedState, setDiffAssignedState] =
    useState<OrgAssignmentState>({ left: [], right: [] });
  const [filterText, setFilterText] = useState("");

  // データソースが変わったら前回のdiffと検索条件を破棄
  useEffect(() => {
    setDiffAssignedState({ left: [], right: [] });
    setFilterText("");
    onDiffChange?.({ left: [], right: [] });
  }, [currentAssignedOrgs]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseOrgs = useMemo(
    () =>
      showAllOrgs
        ? allOrgs.map((c) => ({ id: c.org_id, name: c.name }))
        : invitedOrgs.map((c) => ({ id: c.org_id, name: c.name })),
    [invitedOrgs, allOrgs, showAllOrgs]
  );

  const targetOrgs = useMemo(() => {
    const ids = new Set(baseOrgs.map((o) => o.id));
    return [
      ...baseOrgs,
      ...currentAssignedOrgs
        .filter((c) => !ids.has(c.org_id))
        .map((c) => ({ id: c.org_id, name: c.name })),
    ];
  }, [baseOrgs, currentAssignedOrgs]);

  const initialAssignedState = useMemo(
    () => ({
      left: targetOrgs.filter(
        (org) => !currentAssignedOrgs.find((c) => c.org_id === org.id)
      ),
      right: targetOrgs.filter((org) =>
        currentAssignedOrgs.find((c) => c.org_id === org.id)
      ),
    }),
    [targetOrgs, currentAssignedOrgs]
  );

  const updatedAssignedState = useMemo(() => {
    const movedToRight = new Set(diffAssignedState.right.map((o) => o.id));
    const movedToLeft = new Set(diffAssignedState.left.map((o) => o.id));

    if (movedToRight.size === 0 && movedToLeft.size === 0) {
      return initialAssignedState;
    }

    const initialIds = new Set([
      ...initialAssignedState.left.map((o) => o.id),
      ...initialAssignedState.right.map((o) => o.id),
    ]);
    const extraOrgs = [
      ...diffAssignedState.left,
      ...diffAssignedState.right,
    ].filter((o) => !initialIds.has(o.id));

    const allOrgs = [
      ...initialAssignedState.left,
      ...initialAssignedState.right,
      ...extraOrgs,
    ];
    const initialRightIds = new Set(
      initialAssignedState.right.map((o) => o.id)
    );
    return {
      left: allOrgs.filter(
        (o) =>
          movedToLeft.has(o.id) ||
          (!movedToRight.has(o.id) && !initialRightIds.has(o.id))
      ),
      right: allOrgs.filter(
        (o) =>
          movedToRight.has(o.id) ||
          (!movedToLeft.has(o.id) && initialRightIds.has(o.id))
      ),
    };
  }, [initialAssignedState, diffAssignedState]);

  const diffOrgIds = useMemo(
    () =>
      new Set([
        ...diffAssignedState.left.map((org) => org.id),
        ...diffAssignedState.right.map((org) => org.id),
      ]),
    [diffAssignedState]
  );

  const filteredLeftOrgs = useMemo(() => {
    const normalized = filterText.trim().toLowerCase();
    if (!normalized) return updatedAssignedState.left;

    return updatedAssignedState.left.filter(
      (org) =>
        diffOrgIds.has(org.id) ||
        org.name.toLowerCase().includes(normalized) ||
        org.id.toLowerCase().includes(normalized)
    );
  }, [diffOrgIds, filterText, updatedAssignedState.left]);

  const handleChange = (
    _updated: OrgAssignmentState,
    diff: OrgAssignmentState
  ) => {
    setDiffAssignedState(diff);
    onDiffChange?.(diff);
  };

  return {
    showAllOrgs,
    setShowAllOrgs,
    initialAssignedState,
    updatedAssignedState,
    diffAssignedState,
    filteredLeftOrgs,
    filterText,
    setFilterText,
    handleChange,
    isReady: isCurrentAssignedOrgsLoaded,
    isAllOrgsLoading: showAllOrgs && isAllOrgsLoadStarted && !isAllOrgsLoaded,
  };
};
